import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { ScrollReveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  ["First access to the drop", "You hear about new runs before the public does"],
  ["$23.95/month locked for life", "Founding subscribers never pay more"],
  ["A say in what we build", "Your answers shape the next product"],
];

const QUESTIONS = [
  {
    key: "price_point",
    label: "What would you pay for a 30-strip pack?",
    options: ["$15-20", "$20-25", "$25-30", "$30+"],
  },
  {
    key: "usage_frequency",
    label: "How often would you use the strips?",
    options: ["Every day", "A few times a week", "Training days only", "Nights only"],
  },
  {
    key: "primary_use",
    label: "What would be your primary use?",
    options: ["Training", "Sleep", "Recovery", "All of it"],
  },
  {
    key: "used_before",
    label: "Have you used nasal strips before?",
    options: ["Never", "Tried them", "Using them now"],
  },
];

const Founders = () => {
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState({});
  const [suggestions, setSuggestions] = useState("");
  const [state, setState] = useState("idle");
  const [count, setCount] = useState(null);
  const [postPurchase, setPostPurchase] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/founders/count`)
      .then((res) => setCount(res.data.count))
      .catch(() => setCount(null));
    const surveyEmail = new URLSearchParams(window.location.search).get("survey");
    if (surveyEmail && surveyEmail.includes("@")) {
      setEmail(surveyEmail.trim());
      setPostPurchase(true);
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    const missing = QUESTIONS.some((q) => !answers[q.key]);
    if (missing) {
      toast.error("Answer all four questions — takes 10 seconds.");
      return;
    }
    setState("loading");
    try {
      await axios.post(`${API}/founders`, { email, ...answers, suggestions });
      setState("done");
      setCount((c) => (typeof c === "number" ? c + 1 : c));
      toast.success(postPurchase ? "Answers in — you're shaping the next run." : "You're on the founders list — welcome email incoming.");
    } catch (err) {
      setState("idle");
      if (err.response?.status === 409) {
        toast("You're already a founder.");
      } else if (err.response?.status === 422) {
        toast.error("That email doesn't look right.");
      } else {
        toast.error("Something broke. Try again.");
      }
    }
  };

  return (
    <section
      id="founders"
      data-testid="founders-section"
      className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 stage-light pointer-events-none" />
      <div className="relative max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10">
        <ScrollReveal className="lg:col-span-5">
          <Overline className="mb-4">First Drop — 2026</Overline>
          <h2 className="font-anton uppercase leading-[0.85] text-[clamp(2.75rem,7vw,6.5rem)]">
            Join the
            <br />
            <span className="text-outline-volt">founders</span> list
          </h2>
          <p className="mt-6 text-white/60 font-light leading-relaxed text-base md:text-lg max-w-md">
            Not a waitlist. A founding crew. Get in early, get it cheap, and
            tell us what to build next.
          </p>

          <ul className="mt-10 border-t border-white/10" data-testid="founders-benefits-list">
            {BENEFITS.map(([title, desc], i) => (
              <li key={title} className="border-b border-white/10 py-5 flex gap-5">
                <span className="font-anton text-volt text-lg leading-none mt-1">
                  0{i + 1}
                </span>
                <div>
                  <p className="font-anton uppercase text-base md:text-lg tracking-wide">{title}</p>
                  <p className="text-white/50 font-light text-sm mt-1">{desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <p
            data-testid="founders-count"
            className="mt-8 font-plex text-xs uppercase tracking-[0.25em] text-white/40"
          >
            {count === null || count === 0
              ? "Be the first founder"
              : `${count} ${count === 1 ? "founder" : "founders"} already in`}
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.15} className="lg:col-span-7">
          {state === "done" ? (
            <div
              data-testid="founders-success-message"
              className="border border-volt bg-volt/5 p-8 md:p-12 h-full flex flex-col justify-center"
            >
              <div className="flex items-center gap-4">
                <span className="w-10 h-10 bg-volt flex items-center justify-center">
                  <Check size={20} className="text-black" strokeWidth={3} />
                </span>
                <p className="font-anton uppercase text-2xl md:text-4xl">Founder status: locked in.</p>
              </div>
              <p className="mt-6 text-white/60 font-light text-base md:text-lg max-w-lg">
                {postPurchase ? (
                  <>
                    Answers in — you're officially shaping the next run. Your pack is on its
                    way; watch for the tracking email.
                  </>
                ) : (
                  <>
                    Welcome email on its way — first dibs on new drops, the{" "}
                    <span className="text-volt">$14.95 intro price</span>, and founder pricing
                    locked for life.
                  </>
                )}
              </p>
            </div>
          ) : (
            <form
              data-testid="founders-form"
              onSubmit={onSubmit}
              className="border border-white/10 bg-[#121212] p-7 md:p-10"
            >
              <label
                htmlFor="founders-email"
                className="font-plex text-xs uppercase tracking-[0.25em] text-white/50"
              >
                Your email
              </label>
              <input
                id="founders-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="YOUR@EMAIL.COM"
                data-testid="founders-email-input"
                className="mt-3 w-full h-14 bg-transparent border border-white/20 focus:border-volt px-5 font-plex text-sm uppercase tracking-[0.15em] placeholder:text-white/30 text-white outline-none transition-colors duration-300"
              />

              <div className="mt-9 space-y-8">
                {QUESTIONS.map((q, qi) => (
                  <fieldset key={q.key} data-testid={`founders-question-${q.key}`}>
                    <legend className="font-plex text-xs uppercase tracking-[0.25em] text-white/50 mb-3">
                      0{qi + 1} / {q.label}
                    </legend>
                    <div className="flex flex-wrap gap-2">
                      {q.options.map((opt, oi) => {
                        const active = answers[q.key] === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`founders-q${qi + 1}-option-${oi + 1}`}
                            onClick={() => setAnswers((a) => ({ ...a, [q.key]: opt }))}
                            className={`px-4 py-2.5 border font-plex text-xs uppercase tracking-[0.15em] transition-[background-color,color,border-color,transform] duration-200 active:scale-95 ${
                              active
                                ? "border-volt bg-volt text-black"
                                : "border-white/20 text-white/60 hover:border-white/60 hover:text-white"
                            }`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
                <div data-testid="founders-question-suggestions">
                  <label
                    htmlFor="founders-suggestions"
                    className="font-plex text-xs uppercase tracking-[0.25em] text-white/50 mb-3 block"
                  >
                    05 / Anything you'd like to see from us? (optional)
                  </label>
                  <textarea
                    id="founders-suggestions"
                    value={suggestions}
                    onChange={(e) => setSuggestions(e.target.value)}
                    placeholder="Suggestions, wishlist, things that bug you about other strips — anything goes"
                    data-testid="founders-suggestions-input"
                    rows={3}
                    maxLength={600}
                    className="w-full bg-transparent border border-white/20 focus:border-volt p-4 font-light text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={state === "loading"}
                data-testid="founders-submit-button"
                className="group mt-10 w-full h-16 bg-volt text-black font-anton uppercase tracking-wider text-base md:text-lg flex items-center justify-center gap-3 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-[0.98] disabled:opacity-60"
              >
                {state === "loading" ? "Locking in…" : "Join the founders list"}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </ScrollReveal>
      </div>
    </section>
  );
};

export default Founders;
